/** State machine behind the client-setup wizard.
 *
 *  The wizard is *progressive*: every assignment endpoint needs a `traineeId`, so the trainee is
 *  created the moment identity is valid and each later step saves against the real row. That makes
 *  per-step outcome the central piece of state — the footer needs "can I advance", the summary needs
 *  "what actually got written", and a failed save must keep the coach on the step without losing
 *  what they typed. One reducer owns all of it; the steps stay dumb.
 *
 *  Kept free of React and of every service import so the whole flow is testable as data.
 */

import type { AdminWeightTableWeek } from "../../../api/generated";
import { GOAL_DEFAULT_WEEKLY_RANGE, type GoalValue } from "../../../enums/GoalTypes";

export type StepId = "identity" | "goals" | "nutrition" | "workout" | "checkins" | "summary";

/**
 * `idle` = untouched, `dirty` = edited but not persisted, `skipped` = deliberately left for later,
 * `ready` = filled in and waiting for the commit (create mode only), `failed` = the write was
 * attempted and did not land.
 */
export type StepStatus = "idle" | "dirty" | "ready" | "saving" | "saved" | "skipped" | "failed";

/**
 * `create` collects every step and commits them together when the coach presses סיום; `configure`
 * re-enters an existing client and saves each step as it is passed.
 *
 * The split exists because of the welcome email: the server builds it during `POST /admin/trainee`
 * from the plain-text password, which it then hashes and forgets — there is no resend endpoint. So
 * the only way to stop a brand-new client being emailed before their plan exists is to not create
 * them until the coach has finished choosing. `configure` has nothing to defer: the client already
 * exists and was emailed long ago, so immediate per-step saves are strictly better there.
 */
export type WizardMode = "create" | "configure";

export type CheckInItemType = "BODY_MEASUREMENT" | "BODY_WEIGHT" | "PROGRESS_PHOTO" | "FORM";

/** The shape `getUsersWithoutRoles` returns, narrowed to what the identity step reads. */
export interface ExistingUser {
  id: number;
  name?: string;
  email?: string;
  gender?: "MALE" | "FEMALE";
  phoneNumber?: string;
  companyId?: number;
}

export interface IdentityValues {
  /** `choose` is the fork between promoting an existing user and creating one from scratch. */
  source: "choose" | "existing" | "new";
  existingUser: ExistingUser | null;
  name: string;
  email: string;
  phoneNumber: string;
  gender: "MALE" | "FEMALE" | "";
  /** dayjs objects from `@mui/x-date-pickers`; converted to epoch seconds only at submit. */
  dateOfBirth: unknown | null;
  expirationDate: unknown | null;
  height: string;
  bodyWeight: string;
  companyId: string;
  trainerId: string;
  password: string;
  passwordConfirm: string;
  sendWelcomeEmail: boolean;
}

export interface GoalsValues {
  goal: GoalValue | "";
  weeklyRange: string;
  stepsGoal: string;
  /** Collected in HOURS because that is how a coach thinks; converted to minutes at submit. */
  sleepGoalHours: string;
  caloriesGoal: string;
  /**
   * The current week exactly as the server sent it, spread back on save.
   *
   * `POST /admin/weights/{traineeId}` is a whole-week REPLACE, not a goals patch:
   * `WeightTrackService.updateRowRecord` sets `notes`, `supplements` and the five goal columns
   * unconditionally, then rewrites all seven days through `updateDailyMetric`, whose rule is "a
   * blank clears the value" (except weight/steps that came from the health sync — sleep has no
   * provenance column and always clears, and a day left with nothing is deleted outright).
   *
   * So posting only the goal fields would wipe that week's notes, supplements and every
   * manually-entered weight/steps/sleep — including the initial weigh-in `createTrainee` writes
   * from `bodyWeight` seconds earlier. Read-modify-write is mandatory, exactly as the weight page
   * already does it (`ClientWeightTrackPage.js` spreads the loaded week before posting).
   *
   * `weekDate` is echoed from here rather than computed: the server's own comment on
   * `updateRowRecord` records that a disagreeing anchor misses the row lookup and creates a
   * duplicate week on the wrong day, taking all seven daily writes with it.
   */
  weekRow: AdminWeightTableWeek | null;
}

export interface NutritionValues {
  /** `template` deep-copies a V2 menu template; `macros` writes targets with no meals. */
  choice: "template" | "macros" | null;
  templateId: string | null;
  targets: { calories: string; protein: string; carbs: string; fats: string; fiber: string };
  waterGoalMl: string;
}

export interface WorkoutValues {
  templateId: number | null;
  includeRmWeek: boolean;
  automaticProgression: boolean;
  useProgressionAlgorithm: boolean;
  showRir: boolean;
}

export type CheckInFrequency = "DAILY" | "WEEKLY" | "MONTHLY";

/**
 * The workout step's template-list filters.
 *
 * Not part of `WorkoutValues` on purpose: these narrow what the coach is looking at, they are not
 * something the wizard saves, and putting them in the step's values would send them to the server
 * as part of the assignment payload.
 */
export interface WorkoutFilters {
  keyword: string;
  /** 0 = any; matches the templates library, whose "הכול" option is also 0. */
  numberOfWorkouts: number;
  experienceLevel: string;
  bodyPart: string;
}

export const EMPTY_WORKOUT_FILTERS: WorkoutFilters = {
  keyword: "",
  numberOfWorkouts: 0,
  experienceLevel: "ALL",
  bodyPart: "ALL",
};

export interface CheckInsValues {
  /**
   * `once` sends a single request now (`POST /admin/check-in`); `recurring` creates a schedule
   * that belongs to this client (`POST /admin/scheduled-check-in` with `traineeIds: [id]`).
   *
   * Only ever CREATES a schedule, never edits one. Adding the client to an existing shared
   * schedule would mean a read-modify-write on its `traineeIds` through
   * `PUT /admin/scheduled-check-in/{id}`, which replaces the whole object — two coaches
   * onboarding at the same time would drop each other's clients off the schedule.
   */
  mode: "once" | "recurring";
  itemTypes: CheckInItemType[];
  formTemplateIds: number[];
  frequency: CheckInFrequency;
  /** WEEKLY only — day names as the server's `scheduleDays` enum spells them. */
  scheduleDays: string[];
  /** MONTHLY only. */
  daysOfMonth: number[];
  intervalWeeks: number;
  intervalMonths: number;
  /** "HH:00", matching the hour picker in `ScheduledCheckInDialog`. */
  scheduleTime: string;
}

export interface StepValuesMap {
  identity: IdentityValues;
  goals: GoalsValues;
  nutrition: NutritionValues;
  workout: WorkoutValues;
  checkins: CheckInsValues;
  summary: Record<string, never>;
}

export interface StepState<V> {
  status: StepStatus;
  /** Hebrew, already run through `getErrorMessage`; shown inline on the step, not as a toast. */
  error: string | null;
  values: V;
}

export interface WizardState {
  mode: WizardMode;
  traineeId: number | null;
  current: StepId;
  steps: { [K in StepId]: StepState<StepValuesMap[K]> };
}

export const STEP_ORDER: StepId[] = [
  "identity",
  "goals",
  "nutrition",
  "workout",
  "checkins",
  "summary",
];

/** Exhaustive on purpose — a new step with no Hebrew label is a `typecheck:app` failure, not a
 *  blank rail entry. Same reason `enums/Capabilities.ts` keeps its label maps exhaustive. */
export const STEP_LABELS: Record<StepId, string> = {
  identity: "פרטים אישיים",
  goals: "יעדים",
  nutrition: "תפריט תזונה",
  workout: "תוכנית אימון",
  checkins: "צ'ק-אין וטפסים",
  summary: "סיכום",
};

export const STEP_HINTS: Record<StepId, string> = {
  identity: "שם, יצירת קשר וסיסמה",
  goals: "צעדים, שינה, יעד תזונה ומטרת משקל",
  nutrition: "תבנית תפריט או יעדי מאקרו",
  workout: "בחירת תבנית אימון",
  checkins: "בקשת צ'ק-אין ושליחת טפסים",
  summary: "מה הוגדר ומה נשאר",
};

/**
 * What the rail shows and numbers: always all six, in both modes.
 *
 * Identity stays on the rail in `configure` mode — marked done, not clickable. Dropping it made
 * the numbering restart the instant the client was created: identity was step 1 of 6 on
 * `/clients/new`, then goals became step 1 of 5 on `/clients/:id/setup`, so the counter moved
 * BACKWARDS across the navigation and read as "I'm back at the beginning". Keeping it also tells
 * the truth on a re-entry — an existing client does have its details filled in.
 */
export const railSteps = (): StepId[] => STEP_ORDER;

/**
 * The steps the footer and the rail can actually move between.
 *
 * Identity is excluded in `configure` mode: the client exists and creation cannot be re-run, so
 * next/back/goTo must never land there even though it is drawn on the rail.
 */
export const navigableSteps = (mode: WizardMode): StepId[] =>
  mode === "create" ? STEP_ORDER : STEP_ORDER.filter(step => step !== "identity");

const neighbour = (state: WizardState, offset: number): StepId => {
  const steps = navigableSteps(state.mode);
  const index = steps.indexOf(state.current);
  if (index === -1) return state.current;
  return steps[Math.min(Math.max(index + offset, 0), steps.length - 1)];
};

export const nextStepOf = (state: WizardState): StepId => neighbour(state, 1);
export const prevStepOf = (state: WizardState): StepId => neighbour(state, -1);
export const isFirstStep = (state: WizardState): boolean =>
  navigableSteps(state.mode)[0] === state.current;

const EMPTY_IDENTITY: IdentityValues = {
  source: "choose",
  existingUser: null,
  name: "",
  email: "",
  phoneNumber: "",
  gender: "",
  dateOfBirth: null,
  expirationDate: null,
  height: "",
  bodyWeight: "",
  companyId: "",
  trainerId: "",
  password: "",
  passwordConfirm: "",
  sendWelcomeEmail: true,
};

const EMPTY_GOALS: GoalsValues = {
  goal: "",
  weeklyRange: "",
  stepsGoal: "",
  sleepGoalHours: "",
  caloriesGoal: "",
  weekRow: null,
};

const EMPTY_NUTRITION: NutritionValues = {
  choice: null,
  templateId: null,
  targets: { calories: "", protein: "", carbs: "", fats: "", fiber: "" },
  waterGoalMl: "",
};

/** Mirrors the defaults `PlanSerice.changeProgram` already sends when given no advanced settings. */
const EMPTY_WORKOUT: WorkoutValues = {
  templateId: null,
  includeRmWeek: false,
  automaticProgression: true,
  useProgressionAlgorithm: true,
  showRir: true,
};

const EMPTY_CHECKINS: CheckInsValues = {
  mode: "once",
  itemTypes: [],
  formTemplateIds: [],
  // Same defaults the standalone scheduled-check-in dialog opens with.
  frequency: "WEEKLY",
  scheduleDays: ["SUNDAY"],
  daysOfMonth: [1],
  intervalWeeks: 1,
  intervalMonths: 1,
  scheduleTime: "09:00",
};

const idle = <V,>(values: V): StepState<V> => ({ status: "idle", error: null, values });

export const initialWizardState = (mode: WizardMode, traineeId: number | null = null): WizardState => ({
  mode,
  traineeId,
  current: navigableSteps(mode)[0],
  steps: {
    // `configure` means the client exists: identity is already done, and the rail says so.
    identity:
      mode === "configure"
        ? { status: "saved" as StepStatus, error: null, values: EMPTY_IDENTITY }
        : idle(EMPTY_IDENTITY),
    goals: idle(EMPTY_GOALS),
    nutrition: idle(EMPTY_NUTRITION),
    workout: idle(EMPTY_WORKOUT),
    checkins: idle(EMPTY_CHECKINS),
    summary: idle({}),
  },
});

/** One `patch` variant per step, so `values` is checked against *that* step's shape. A single
 *  `{step: StepId; values: Partial<unknown>}` would type-check a goals payload sent to `workout`. */
type PatchAction = {
  [K in StepId]: { type: "patch"; step: K; values: Partial<StepValuesMap[K]> };
}[StepId];

type HydrateAction = {
  type: "hydrate";
  steps: Partial<{
    [K in StepId]: { status?: StepStatus; values?: Partial<StepValuesMap[K]> };
  }>;
};

/** Per-step result of a create-mode commit, in the order `commitNewClient` attempted them. */
export type CommitResults = Partial<Record<StepId, { status: StepStatus; error?: string }>>;

export type WizardAction =
  | PatchAction
  | HydrateAction
  | { type: "created"; traineeId: number }
  | { type: "advance"; step: StepId }
  | { type: "commitStart" }
  | { type: "commitFinished"; traineeId: number; results: CommitResults }
  | { type: "saveStart"; step: StepId }
  | { type: "saveSuccess"; step: StepId }
  | { type: "saveFailure"; step: StepId; error: string }
  | { type: "skip"; step: StepId }
  | { type: "goTo"; step: StepId }
  | { type: "back" };

/** The value shape of *some* step — what you have left once `K` is no longer statically known. */
type AnyStepValues = StepValuesMap[StepId];

/** The one place the per-step typing is deliberately loosened.
 *
 *  `WizardAction` keeps `step` and `values` correlated at every CALL site, which is the point of the
 *  mapped-union `PatchAction`. Inside the reducer that correlation is gone — `action.step` is the
 *  whole `StepId` union — and TypeScript cannot express "this key's value type". Rather than
 *  scatter casts through every branch, the unsoundness is confined to this helper and the branches
 *  below hand it an already-merged object. */
const withStep = (
  state: WizardState,
  step: StepId,
  patch: Partial<StepState<AnyStepValues>>,
): WizardState => ({
  ...state,
  steps: {
    ...state.steps,
    [step]: { ...state.steps[step], ...patch },
  } as WizardState["steps"],
});

export const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case "patch": {
      const step = state.steps[action.step];
      const values: Record<string, unknown> = { ...step.values, ...action.values };
      // Choosing a goal fills the weekly range the company uses for it, so the coach is not asked
      // to restate the same decision in a second dropdown. An explicit range in the same patch
      // wins — that is the coach overriding the default on purpose.
      if (action.step === "goals") {
        const incoming = action.values as Partial<GoalsValues>;
        if (incoming.goal && incoming.weeklyRange === undefined) {
          values.weeklyRange = GOAL_DEFAULT_WEEKLY_RANGE[incoming.goal] ?? values.weeklyRange;
        }
      }
      return withStep(state, action.step, {
        // A save in flight must not be downgraded to `dirty` by a late keystroke.
        status: step.status === "saving" ? "saving" : "dirty",
        error: null,
        values: values as AnyStepValues,
      });
    }

    case "hydrate": {
      // `configure` mode mounts against a client that may already have a program, a menu and a
      // goals row. Hydration marks those steps `saved` so the rail reports reality.
      let next = state;
      (Object.keys(action.steps) as StepId[]).forEach(step => {
        const incoming = action.steps[step];
        if (!incoming) return;
        next = withStep(next, step, {
          status: incoming.status ?? next.steps[step].status,
          values: { ...next.steps[step].values, ...incoming.values } as AnyStepValues,
        });
      });
      return next;
    }

    case "created": {
      const created = withStep({ ...state, traineeId: action.traineeId }, "identity", {
        status: "saved",
        error: null,
      });
      return { ...created, current: nextStepOf(created) };
    }

    // create mode: the step is filled in and queued for the commit, nothing was written yet.
    case "advance": {
      const ready = withStep(state, action.step, { status: "ready", error: null });
      if (state.current !== action.step) return ready;
      return { ...ready, current: nextStepOf(ready) };
    }

    case "commitStart":
      return withStep(state, "summary", { status: "saving", error: null });

    case "commitFinished": {
      let next: WizardState = { ...state, traineeId: action.traineeId };
      (Object.keys(action.results) as StepId[]).forEach(step => {
        const result = action.results[step];
        if (!result) return;
        next = withStep(next, step, { status: result.status, error: result.error ?? null });
      });
      return withStep(next, "summary", { status: "saved", error: null });
    }

    case "saveStart":
      return withStep(state, action.step, { status: "saving", error: null });

    case "saveSuccess": {
      const saved = withStep(state, action.step, { status: "saved", error: null });
      // Advance only if the coach is still standing on the step that finished saving.
      if (state.current !== action.step) return saved;
      return { ...saved, current: nextStepOf(saved) };
    }

    case "saveFailure":
      // Stays on the step with the values intact — re-submitting is the recovery.
      return withStep(state, action.step, { status: "failed", error: action.error });

    case "skip": {
      const skipped = withStep(state, action.step, { status: "skipped", error: null });
      if (state.current !== action.step) return skipped;
      return { ...skipped, current: nextStepOf(skipped) };
    }

    case "goTo":
      return navigableSteps(state.mode).includes(action.step)
        ? { ...state, current: action.step }
        : state;

    case "back":
      return { ...state, current: prevStepOf(state) };

    default:
      return state;
  }
};

/** What the summary step renders: one row per configurable step, in flow order. */
export const stepOutcomes = (state: WizardState): Array<{ step: StepId; status: StepStatus }> =>
  railSteps()
    .filter(step => step !== "summary")
    .map(step => ({ step, status: state.steps[step].status }));
